/**
 * useDeletedCount Hook
 *
 * Fetches the count of soft-deleted records for an endpoint.
 * Used to display a badge on the "Show Deleted" toggle in the toolbar.
 *
 * This hook is INTERNAL to MetadataTable - not exported publicly.
 */

import { useQuery } from '@tanstack/react-query'
import { tableService } from '@/services/tableService'
import type { Filter } from '@/types/api.types'

/**
 * Options for useDeletedCount hook
 */
export interface UseDeletedCountOptions {
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Whether to enable the query (default: true) */
  enabled?: boolean
  /** Additional base filters to apply alongside deleted filter */
  baseFilters?: Filter[]
}

/**
 * Return type from useDeletedCount hook
 */
export interface UseDeletedCountResult {
  /** Count of deleted records */
  count: number
  /** Loading state */
  isLoading: boolean
  /** Error state */
  error: Error | null
  /** Refetch the count */
  refetch: () => void
}

/**
 * Hook to fetch the count of soft-deleted records for an endpoint.
 *
 * @param options - Configuration options
 * @returns Count, loading state, and refetch function
 *
 * @example
 * ```tsx
 * const { count, isLoading } = useDeletedCount({
 *   endpoint: '/offerings',
 *   enabled: recordActionsEnabled, // Only fetch if delete/undelete actions exist
 * })
 *
 * // Show badge on trash toggle
 * <Badge count={count}>
 *   <Switch checked={showDeleted} onChange={setShowDeleted} />
 *   <span>Show Deleted</span>
 * </Badge>
 * ```
 */
export function useDeletedCount({
  endpoint,
  enabled = true,
  baseFilters = [],
}: UseDeletedCountOptions): UseDeletedCountResult {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['deletedCount', endpoint, baseFilters],
    queryFn: async () => {
      // Query for deleted records (deletedAt IS NOT NULL)
      const deletedFilter: Filter = {
        key: 'deletedAt',
        constraint_type: 'IS_NOT_NULL',
      }

      // Combine with any additional base filters
      const filters = [...baseFilters, deletedFilter]

      // Request just the count, not the actual data
      // Must include includeDeleted: true to bypass service layer's default filter
      const response = await tableService.search(endpoint, {
        filters,
        limit: 0, // We only need the count, not the data
        offset: 0,
        includeDeleted: true,
      })

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch deleted count')
      }

      // Return the total count from meta
      return response.meta?.total ?? 0
    },
    enabled,
    staleTime: 1000 * 60, // 1 minute - deleted count can change
    refetchOnWindowFocus: false, // Don't refetch on focus, just on explicit refetch
  })

  return {
    count: data ?? 0,
    isLoading,
    error: error as Error | null,
    refetch,
  }
}
